// What the sentence analyser (वाक्यविश्लेषणम्) says about one reading of a word: its part of speech, a short gloss
// for the word-by-word line, a compact label for the reading chips, and plain notes on tense, person, number and role.
// Grammar names come from the engine's labels; the short explanations here are teaching text, never Prakrit forms.

import type { Analysis } from '@/prakrit/lib/grammar/analyser'
import type { Role } from '@/prakrit/lib/grammar/sentence'
import type {
    Case,
    Lang,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'

import {
    CASE_LABEL,
    GENDER_SHORT,
    KARAK,
    KRIDANTA_LABEL,
    NUMBER_LABEL,
    PERSON_SHORT,
    SUBJECT_GLOSS,
    TENSE_SHORT
} from '@/prakrit/lib/grammar/labels'

const lang = (hi: string, en: string): Lang => ({ hi, en })

/** The text inside the first pair of brackets: "अधिकरण (में, पर)" → "में, पर". */
const inner = (text: string): string | null => /\(([^)]*)\)/.exec(text)?.[1] ?? null

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/** The part-of-speech pill on a word card. */
export const posOf = (a: Analysis): Lang => {
    switch (a.pos) {
        case 'pronoun':
            return lang('सर्वनाम', 'Pronoun')
        case 'noun':
        case 'stem':
            return lang('संज्ञा', 'Noun')
        case 'verb':
        case 'as':
            return lang('क्रिया', 'Verb')
        case 'avyaya':
            return lang('अव्यय', 'Indeclinable')
        case 'kridanta':
        case 'irregular':
            return lang('कृदन्त', 'Participle')
        case 'number':
            return lang('संख्या', 'Number')
        case 'dhatu':
            return lang('धातु', 'Root')
    }
}

/** What each काल (tense or mood) is used for, in a few words. */
export const TENSE_MEANING: Record<Tense, Lang> = {
    vartaman: lang('अभी, या रोज़', 'now, or habitually'),
    bhoot: lang('पहले हो चुका', 'it already happened'),
    bhavishya: lang('आगे होगा', 'it will happen'),
    vidhi: lang('आज्ञा, इच्छा या उपदेश', 'a command, wish or advice'),
    karmani_vartaman: lang('काम किया जा रहा है', 'it is being done'),
    karmani_bhoot: lang('काम किया गया', 'it was done'),
    karmani_vidhi: lang('काम किया जाए', 'let it be done'),
    prerak_vartaman: lang('किसी से करवाता है', 'makes someone do it'),
    prerak_bhoot: lang('किसी से करवाया', 'made someone do it'),
    prerak_bhavishya: lang('किसी से करवाएगा', 'will make someone do it'),
    prerak_vidhi: lang('किसी से करवाओ', 'have someone do it'),
    kriyatipatti: lang('ऐसा होता तो…', 'if it had happened…')
}

/** Tense name with its meaning: "वर्तमान काल (अभी, या रोज़)" · "Present (now, or habitually)". */
export const tenseText = (tense: Tense, name: Lang): Lang => lang(
    `${name.hi} (${TENSE_MEANING[tense].hi})`,
    `${capital(name.en)} (${TENSE_MEANING[tense].en})`
)

/** पुरुष in plain English (the course says उत्तम, मध्यम, अन्य rather than first, second, third). */
const PERSON_EN: Record<Person, string> = {
    uttam: 'the speaker (I, we)',
    madhyam: 'the one spoken to (you)',
    anya: 'the one spoken about (he, she, it, they)'
}

const NUMBER_EN: Record<Num, string> = {
    eka: 'singular (one)',
    bahu: 'plural (more than one)'
}

export const personText = (p: Person): Lang => lang(`${PERSON_SHORT[p].hi} पुरुष`, capital(PERSON_EN[p]))

export const personNumberText = (p: Person, n: Num): Lang => lang(
    `${PERSON_SHORT[p].hi} पुरुष, ${NUMBER_LABEL[n].hi}`,
    `${capital(PERSON_EN[p])}, ${NUMBER_EN[n]}`
)

export const caseNumberText = (c: Case, n: Num): Lang => lang(
    `${CASE_LABEL[c].hi}, ${NUMBER_LABEL[n].hi}`,
    `${capital(CASE_LABEL[c].en)}, ${NUMBER_EN[n]}`
)

/** Who does the action, read from a verb ending: मैं · I, वे · they … */
export const whoFromEnding = (p: Person, n: Num): Lang => SUBJECT_GLOSS[p][n]

export interface RoleText {
    name: Lang
    question: Lang
}

const karakName = (c: Case): string => KARAK[c].hi.split(' (')[0]

/** The कारक each case marks and the question it answers. */
export const CASE_ROLE: Record<Case, RoleText> = {
    prathama: { name: lang(karakName('prathama'), 'subject, the doer'), question: lang('कौन करता है?', 'Who does it?') },
    dvitiya: { name: lang(karakName('dvitiya'), 'object'), question: lang('किसे? क्या?', 'Whom? What?') },
    tritiya: { name: lang(karakName('tritiya'), 'means or company'), question: lang('किससे? किसके साथ?', 'With what? With whom?') },
    chaturthi: { name: lang(karakName('chaturthi'), 'the receiver'), question: lang('किसके लिए? किसको?', 'For whom? To whom?') },
    panchami: { name: lang(karakName('panchami'), 'the source'), question: lang('कहाँ से? किससे?', 'From where? From whom?') },
    shashthi: { name: lang(karakName('shashthi'), 'belonging'), question: lang('किसका?', 'Whose? Of what?') },
    saptami: { name: lang(karakName('saptami'), 'place or time'), question: lang('कहाँ? कब?', 'Where? When?') },
    sambodhan: { name: lang(karakName('sambodhan'), 'calling someone'), question: lang('किसे पुकारा?', 'Who is called?') }
}

const PREDICATE_ROLE: RoleText = { name: lang('विधेय', 'complement'), question: lang('कर्ता क्या है?', 'What is the subject?') }

/** The likely role of a noun, pronoun or number reading (null for verbs, अव्यय and the rest). */
export const roleText = (a: Analysis, role: Role | null): RoleText | null => {
    if (role === 'predicate') return PREDICATE_ROLE

    if (a.pos === 'noun' || a.pos === 'pronoun') {
        if (role === 'subject') return CASE_ROLE.prathama
        if (role === 'object') return CASE_ROLE.dvitiya

        return CASE_ROLE[a.case]
    }

    if (a.pos === 'number' && role === 'subject') return CASE_ROLE.prathama

    return null
}

/** A short hint for the cases whose meaning a bare gloss does not carry: "में, पर" · "in / on". */
const caseHint = (c: Case, pronoun: boolean): Lang | null => {
    if (c === 'prathama') return null
    if (c === 'dvitiya') return pronoun ? lang('कर्म', 'object') : null

    return lang(inner(KARAK[c].hi) ?? karakName(c), inner(KARAK[c].en) ?? KARAK[c].en)
}

const withHint = (base: Lang, hint: Lang | null): Lang => (hint ? lang(`${base.hi} (${hint.hi})`, `${base.en} (${hint.en})`) : base)

/** "वह, वे (पुल्लिंग)" → वह (एकवचन) or वे (बहुवचन). */
const pickNumber = (text: string, n: Num): string => {
    const parts = text.replace(/\s*\([^)]*\)\s*$/, '').split(/,\s*/)

    return (n === 'bahu' ? parts[1] : parts[0]) ?? parts[0] ?? text
}

/** The word-by-word gloss under each word (never a translation of the sentence). */
export const glossOf = (a: Analysis | null): Lang => {
    if (!a) return lang('अज्ञात', 'not known yet')

    switch (a.pos) {
        case 'noun':
            return withHint(lang(a.hi, a.en), caseHint(a.case, false))
        case 'pronoun': {
            const base = a.person === 'anya'
                ? lang(pickNumber(a.hi, a.number), pickNumber(a.en, a.number))
                : SUBJECT_GLOSS[a.person][a.number]

            return withHint(base, caseHint(a.case, true))
        }
        case 'verb':
        case 'as': {
            const who = SUBJECT_GLOSS[a.person][a.number]
            const tense = a.tense === 'vartaman' ? null : TENSE_SHORT[a.tense]
            const base = a.en.replace(/^to /, '')

            return lang(
                `${a.hi} (${who.hi}${tense ? `, ${tense.hi}` : ''})`,
                `${base} (${who.en}${tense ? `, ${tense.en}` : ''})`
            )
        }
        case 'kridanta': {
            const label = KRIDANTA_LABEL[a.kind]

            return lang(`${a.hi} · ${inner(label.hi) ?? label.hi}`, `${a.en.replace(/^to /, '')} · ${label.en}`)
        }
        case 'dhatu':
            return lang(`${a.hi} (धातु)`, `${a.en} (root)`)
        default:
            return lang(a.hi, a.en)
    }
}

/** A compact Devanagari label for one reading, for the "possible readings" chips. */
export const readingLabel = (a: Analysis): string => {
    switch (a.pos) {
        case 'noun':
            return `${a.lemma} · ${GENDER_SHORT[a.gender]} · ${CASE_LABEL[a.case].hi} · ${NUMBER_LABEL[a.number].hi}`
        case 'pronoun':
            return `${a.base}${a.gender ? ` · ${GENDER_SHORT[a.gender]}` : ''} · ${CASE_LABEL[a.case].hi} · ${NUMBER_LABEL[a.number].hi}`
        case 'verb':
        case 'as':
            return `${a.lemma} · ${TENSE_SHORT[a.tense].hi} · ${PERSON_SHORT[a.person].hi} · ${NUMBER_LABEL[a.number].hi}`
        case 'kridanta':
            return `${a.lemma} · ${KRIDANTA_LABEL[a.kind].hi}`
        case 'irregular':
            return `${a.root ?? a.lemma} · ${a.kind === 'bhoot_kridanta' ? 'भूत कृदन्त' : 'सम्बन्ध कृदन्त'}`
        case 'avyaya':
            return `${a.lemma} · अव्यय`
        case 'number':
            return `${a.lemma} · संख्या`
        case 'dhatu':
            return `${a.lemma} · धातु`
        case 'stem':
            return `${a.lemma} · मूल शब्द`
    }
}
